import { Alert, Box, Card, InputAdornment, inputLabelClasses, Snackbar, Stack, Switch, TextareaAutosize, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FileParameter, GetStockItemsRequest, GetStockItemsWithPriceRequest, GetWarehouseProductsListPageRequest, GetWarehouseProductsWithPriceListPageRequest, SaveListWithItemsRequest, StockItemDTO, WarehouseDTO, WarehouseProductListPageDTO, WarehouseVovClient } from '../../../ApiOld/Api/ApiServerVov';
import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../../../ApiOld/Api/useApi';
import { useNavigate, useParams } from 'react-router-dom';
import CustomSplitter from '../../Shared/Layouts/CustomSplitter';
import MuiDatagrid from '../../Shared/MuiDatagrid';
import { GridColDef, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DeleteOutlined, SearchOutlined } from '@mui/icons-material';
import DecimalValueColumnField from './DecimalValueColumnField';
import { useUserLayoutContext } from '../../../Contexts/Providers/UserLayoutMenuProvider';
import WarehouseActionMenuButtons from './WarehouseActionMenuButtons';
import { ActionType } from './Enums/ActionType';
import NoDataPlaceholder from '../../Shared/Layouts/NoDataPlaceholder';
import WarehouseSignatureDialog from './Dialogs/WarehouseSignatureDialog';
import { useMediaPredicate } from 'react-media-hook';
import WarehouseByBuildingIdSelect from '../../Shared/WarehouseByBuildingIdSelect';
import BuildingWithWarehouseSelect from '../../Shared/BuildingWithWarehouseSelect';

interface WarehouseOtherProps {
    buildingId?: number;
    warehouseId?: number;
    pStockListId?: number;
    actionType: ActionType;
}

export default function WarehouseOther(props: Readonly<WarehouseOtherProps>) {
    const { buildingId, warehouseId, pStockListId, actionType } = props;
    const { authorizedApi } = useApi(WarehouseVovClient);
    const navigate = useNavigate();
    const { setMenuButtons, setBackMenuFunction, setTitle } = useUserLayoutContext();
    const [withPrice, setWithPrice] = React.useState(false);
    const [stockListId, setStockListId] = useState<number | undefined>();
    const [warehouseProducts, setWarehouseProducts] = useState<WarehouseProductListPageDTO[] | undefined>();
    const [warehouseItemsSelectionModel, setWarehouseItemsSelectionModel] = useState<GridRowSelectionModel>();
    const [products, setProducts] = useState<StockItemDTO[]>();
    const [notes, setNotes] = useState<string>('');
    const [datetime, setDatetime] = useState<Date | undefined>(new Date());
    const [warehousesData, setWarehousesData] = useState<WarehouseDTO[]>([]);
    const [selectedBuildingId, setSelectedBuildingId] = useState<number | undefined>(undefined);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | undefined>(undefined);
    const [openSignature, setOpenSignature] = React.useState(false);
    const [stockInOutListSign, setStockInOutListSign] = React.useState(false);
    const [searchText, setSearchText] = useState("");
    const biggerThan700 = useMediaPredicate("(min-width: 800px)");
    const [error, setError] = useState('');
    const { t } = useTranslation();
    const params = useParams();
    const theme = useTheme();
    const counterRef = useRef(0);
    const savingInProgressRef = useRef(false);
    const addItemFromMouseDownRef = useRef(true);
    const apiRef = useGridApiRef();
    let gridApiRef = useGridApiRef();

    const redirectToList = React.useCallback(() => {
        navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseDetail/' + warehouseId);
    }, [navigate, params, warehouseId]);

    const redirectToInWarehouseList = React.useCallback(() => {
        navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseDetail/' + selectedWarehouseId);
    }, [navigate, params, selectedWarehouseId]);

    useEffect(() => {
        if (params && authorizedApi && warehouseId) {
            setBackMenuFunction(() => () => { redirectToList() })
            if (withPrice) {
                authorizedApi.getWarehouseProductsWithPriceListPage(new GetWarehouseProductsWithPriceListPageRequest({ lang: params['lang'] ?? '', warehouseId: warehouseId })).then((r) => {
                    setWarehouseProducts(r ? r.warehouseProductsListPage : []);
                });
            }
            else {
                authorizedApi.getWarehouseProductsListPage(new GetWarehouseProductsListPageRequest({ lang: params['lang'] ?? '', warehouseId: warehouseId })).then((r) => {
                    setWarehouseProducts(r ? r.warehouseProductsListPage : []);
                });
            }
            authorizedApi.getWarehouseDetail(warehouseId).then((d) => {
                setTitle(d.name ?? '');
                setStockInOutListSign(d.stockInOutListSign ?? false);
            });
        }
    }, [params, authorizedApi, warehouseId, setBackMenuFunction, redirectToList, withPrice]);

    useEffect(() => {
        if (params && buildingId && buildingId > 0) {
            setSelectedBuildingId(buildingId);
            loadWarehousesData(buildingId);
        }
    }, [params, buildingId]);

    useEffect(() => {
        if (params && authorizedApi && ((pStockListId && pStockListId > 0) || stockListId)) {
            if (withPrice) {
                authorizedApi.getStockItemsWithPrice(new GetStockItemsWithPriceRequest({ lang: params['lang'] ?? '', stockListId: (pStockListId && pStockListId > 0) ? pStockListId : stockListId! })).then((r) => {
                    loadWarehousesData(r.toWarehouseBuildingId);
                    setStockListId((pStockListId && pStockListId > 0) ? pStockListId : stockListId!);
                    setSelectedBuildingId(r.toWarehouseBuildingId);
                    setSelectedWarehouseId(r.toWarehouseId);
                    setDatetime(r.datetime);
                    setNotes(r.note ?? '');
                    setProducts(r.stockItems?.map(p => StockItemDTO.fromJS(p)));
                });
            }
            else {
                authorizedApi.getStockItems(new GetStockItemsRequest({ lang: params['lang'] ?? '', stockListId: (pStockListId && pStockListId > 0) ? pStockListId : stockListId! })).then((r) => {
                    loadWarehousesData(r.toWarehouseBuildingId);
                    setStockListId((pStockListId && pStockListId > 0) ? pStockListId : stockListId!);
                    setSelectedBuildingId(r.toWarehouseBuildingId);
                    setSelectedWarehouseId(r.toWarehouseId);
                    setDatetime(r.datetime);
                    setNotes(r.note ?? '');
                    setProducts(r.stockItems?.map(p => StockItemDTO.fromJS(p)));
                });
            }
        }
        else {
            setProducts([]);
        }
    }, [params, authorizedApi, pStockListId, withPrice]);

    useEffect(() => {
        setMenuButtons(<WarehouseActionMenuButtons
            allowSaveAndCompleted={((products ?? []).length !== 0) && !((actionType === ActionType.Block || actionType === ActionType.Out) && (selectedWarehouseId === undefined || warehousesData.filter(w => w.id === selectedWarehouseId).length === 0))}
            handleSave={handleSave}
            handleCompleted={handleCompleted}
            handleDeleted={handleDeleted}
        />);
    }, [setMenuButtons, warehouseId, products, notes, datetime, warehousesData, selectedWarehouseId, stockListId, withPrice]);

    useEffect(() => {
        const unsubscribe = apiRef.current.subscribeEvent('cellMouseDown', handleEvent);
        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if ((warehouseItemsSelectionModel?.length ?? 0) === 0)
            addItemFromMouseDownRef.current = true;
    }, [warehouseItemsSelectionModel]);

    useEffect(() => {
        loadWarehousesData(selectedBuildingId);
    }, [selectedBuildingId]);

    const loadWarehousesData = async (buildingId: number | undefined) => {
        authorizedApi.getWarehousesByBuildingId(buildingId ?? 0).then((r) => {
            setWarehousesData(r.warehouses ?? []);
        });
    }

    const columnsItemsInStock: GridColDef[] = [
        { field: 'code', headerName: t('code'), flex: 1, minWidth: 100, editable: false },
        { field: 'name', headerName: t('Name'), flex: 1, minWidth: 100, editable: false },
        { field: 'stockManufacturerName', headerName: t('Manufacturer'), flex: 1, minWidth: 100, editable: false },
        { field: 'stockProductCategoryName', headerName: t('warehouses.productCategories'), flex: 1, minWidth: 100, editable: false },
        { field: 'totalUnitsCount', headerName: t('warehouses.supply'), flex: 1, minWidth: 100, editable: false, renderCell: (params) => params.id !== 'footer' && `${new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) + (params.row.measureUnitAbbr ? ' ' + params.row.measureUnitAbbr : '')}` },
        ...(withPrice ? [{ field: 'price', headerName: t('warehouses.price'), flex: 1, minWidth: 100, editable: false, renderCell: (params) => `${new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)} Kč` }] : []),
    ];

    const columnsWriteOffProducts: GridColDef[] = [
        { field: 'code', headerName: t('code'), flex: 1, minWidth: 100, editable: false },
        { field: 'name', headerName: t('Name'), flex: 2, minWidth: 100, editable: false },
        { field: 'unitsCount', headerName: t('warehouses.unitsCount'), flex: 1, minWidth: 100, cellClassName: 'decimal-cell', renderCell: (params) => { return params.id !== 'footer' && <DecimalValueColumnField isPurchase={false} params={params} isUnitPrice={false} gridApiRef={gridApiRef} t={t} updatedRow={handeUpdatedRow} setError={setError} />; } },
        { field: 'totalUnitsCount', headerName: t('warehouses.available'), flex: 2, minWidth: 100, editable: false, renderCell: (params) => params.id !== 'footer' && `/ ${new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) + (params.row.measureUnitAbbr ? ' ' + params.row.measureUnitAbbr : '')}` },
        ...(withPrice ? [
            { field: 'unitsPrice', headerName: t('warehouses.price'), flex: 1, minWidth: 100, editable: false, renderCell: (params) => params.id !== 'footer' && `${new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)} Kč` },
            { field: 'totalValue', headerName: t('warehouses.totalPrice'), flex: 1, minWidth: 100, editable: false, renderCell: (params) => `${new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.row.unitsCount * params.row.unitsPrice)} Kč` }
        ] : []),
        {
            field: 'delete',
            headerName: '',
            sortable: false,
            width: 40,
            renderCell: (params) => (
                params.id !== 'footer' && (
                    <DeleteOutlined
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDelete(params.id)}
                    />
                )
            ),
        },
    ];

    const calculateTotalValueSum = (products: StockItemDTO[] | undefined) => {
        return products?.reduce((sum, product) => sum + ((product.unitsCount ?? 0) * (product.unitsPrice ?? 0)), 0) ?? 0;
    };

    const totalValueSum = calculateTotalValueSum(products);

    const footerRow = {
        id: 'footer',
        code: '',
        name: '',
        unitsCount: totalValueSum,
        totalUnitsCount: totalValueSum,
        totalUnits: '',
        delete: '',
        ...(withPrice ? { unitsPrice: 1 } : {}),
    };

    const rowsWithFooter = withPrice ? [...(products ?? []), footerRow] : [...(products ?? [])];

    const handeUpdatedRow = (row) => {
        setProducts((products) => products?.map((p) => p.id === row.id ? row : p));
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        if ((warehouseItemsSelectionModel?.length ?? 0) === 0) {
            e.preventDefault();
            return;
        }

        const dragImage = document.createElement('div'); // Create a custom drag image
        dragImage.textContent = 'Vybrané položky';
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-9999px'; // Move it off-screen
        document.body.appendChild(dragImage); // Add to the DOM
        e.dataTransfer.setDragImage(dragImage, 0, 0); // Use it as the drag image
        setTimeout(() => document.body.removeChild(dragImage), 0); // Clean up
    };

    const handleDragEnd = () => {
        if (warehouseItemsSelectionModel) {
            const selectedItems = warehouseItemsSelectionModel
                .map((id) => warehouseProducts?.find((p) => p.id === id))
                .filter((p): p is WarehouseProductListPageDTO => p !== undefined);

            // Ensure there is only one unique productId in selectedItems
            const uniqueSelectedItems = Array.from(
                withPrice ? new Map(selectedItems.map(item => [`${item.stockProductId}-${item.price}`, item])).values() : new Map(selectedItems.map(item => [item.stockProductId, item])).values()
            );

            if (uniqueSelectedItems) {
                const stockItems = uniqueSelectedItems.map((product) => {
                    const totalUnitsCount = warehouseProducts?.reduce((sum, warehouseProduct) => {
                        return warehouseProduct.stockProductId === product.stockProductId && (withPrice ? warehouseProduct.price === product.price : true) ? sum + (warehouseProduct.totalUnitsCount ?? 0) : sum;
                    }, 0);

                    return new StockItemDTO({
                        id: --counterRef.current,
                        productId: product.stockProductId,
                        code: product.code,
                        name: product.name,
                        unitsCount: 1,
                        unitsPrice: product.price,
                        totalUnitsCount: totalUnitsCount ?? 0,
                        measureUnitAbbr: product.measureUnitAbbr,
                    });
                });

                // Ensure that the productId is only added once in writeOffProducts
                if (withPrice) {
                    setProducts((products) => {
                        const existingProductIds = new Set(products?.map(item => `${item.productId}-${item.unitsPrice}`) ?? []);
                        const newItems = stockItems.filter(item => !existingProductIds.has(`${item.productId}-${item.unitsPrice}`));

                        return [...(products ?? []), ...newItems];
                    });
                }
                else {
                    setProducts((products) => {
                        const existingProductIds = new Set(products?.map(item => item.productId) ?? []);
                        const newItems = stockItems.filter(item => !existingProductIds.has(item.productId));

                        return [...(products ?? []), ...newItems];
                    });
                }

                setWarehouseItemsSelectionModel([]);
            }
        }
    };

    const handleDelete = (id) => {
        const updatedProducts = products?.filter((p) => p.id !== id);
        if (updatedProducts)
            gridApiRef.current?.setRows(updatedProducts);
        setProducts(updatedProducts);
    };

    const handleSave = () => {
        if (!savingInProgressRef.current) {
            savingInProgressRef.current = true;
            saveListWithItems(false, getActionTypeName(false), undefined);
        }
    };

    const handleCompleted = () => {
        setOpenSignature(true);
    };

    const saveListWithItems = (isCompleted: boolean, state: string, file: FileParameter | undefined) => {
        const productsToJSON = products?.map(product =>
            product instanceof StockItemDTO ? product : StockItemDTO.fromJS(product)
        ) ?? [];

        authorizedApi.saveListWithItems(new SaveListWithItemsRequest({
            warehouseId: warehouseId,
            toWarehouseId: selectedWarehouseId,
            stockListId: stockListId,
            state: state,
            note: notes ?? '',
            datetime: datetime,
            products: productsToJSON,
            withPrice: withPrice
        })).then((r) => {
            if (r.ok) {
                if (isCompleted) {
                    if (file)
                        authorizedApi.updateStockListSignature(r.id, false, file);
                    if (actionType === ActionType.Out)
                        redirectToInWarehouseList();
                    else
                        redirectToList();
                }
                else
                    setStockListId(r.id);
            }
            else if (r.errorCode === 2) {
                setError(t('warehouses.ItemsTotalCountError'));
            }
        }).finally(() => {
            savingInProgressRef.current = false;
        });
    };

    const handleDeleted = () => {
        if (stockListId) {
            authorizedApi.deleteListWithItems(stockListId).then(() => {
                redirectToList();
            });
        } else {
            redirectToList();
        }
    };

    const getActionTypeName = (isCompleted: boolean) => {
        switch (actionType) {
            case ActionType.Block:
                return isCompleted ? 'block' : 'draft_block';
            case ActionType.Out:
                return isCompleted ? 'out' : 'draft_out';
            case ActionType.Utility:
                return isCompleted ? 'utility' : 'draft_utility';
            case ActionType.WriteOff:
                return isCompleted ? 'write_off' : 'draft_write_off';
            default:
                return '';
        }
    }

    const getActionName = () => {
        switch (actionType) {
            case ActionType.Block:
                return t('warehouses.reservation');
            case ActionType.Out:
                return t('warehouses.disbursement');
            case ActionType.Utility:
                return t('warehouses.consumption')
            case ActionType.WriteOff:
                return t('warehouses.writeOff');
            default:
                return '';
        }
    }

    const handleEvent = (event) => {
        if (addItemFromMouseDownRef.current) {
            setWarehouseItemsSelectionModel([event.row.id]);
        }
    };

    const removeDiacritics = (str) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };

    const filteredRows = (warehouseProducts ?? []).filter(
        (row) =>
            removeDiacritics((row.name ?? '').toLowerCase()).includes(removeDiacritics(searchText.toLowerCase())) ||
            removeDiacritics((row.code ?? '').toLowerCase()).includes(removeDiacritics(searchText.toLowerCase())) ||
            removeDiacritics((row.stockManufacturerName ?? '').toLowerCase()).includes(removeDiacritics(searchText.toLowerCase())) ||
            removeDiacritics((row.stockProductCategoryName ?? '').toLowerCase()).includes(removeDiacritics(searchText.toLowerCase())) ||
            removeDiacritics((row.totalUnitsCount ?? '').toString().toLowerCase()).includes(removeDiacritics(searchText.toLowerCase())) ||
            removeDiacritics((row.price ?? '').toString().toLowerCase()).includes(removeDiacritics(searchText.toLowerCase()))
    );

    return <Box sx={{ maxWidth: '100vw' }}>
        <CustomSplitter>
            <Card sx={{ margin: 3, padding: 3, height: 'calc(100vh - 225px)' }} elevation={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ overflow: 'auto' }} draggable onDragStart={handleDragStart}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                            <TextField label={t('warehouses.searchInProducts')}
                                InputLabelProps={{
                                    sx: {
                                        [`&.${inputLabelClasses.shrink}`]: {
                                            // set the color of the label when shrinked (usually when the TextField is focused)
                                            color: "gray",
                                            marginTop: '2px',
                                            fontSize: '13px'
                                        }
                                    }
                                }}
                                sx={{
                                    border: '0px solid white',
                                    mt: '4px',
                                    mb: '8px',
                                    minWidth: biggerThan700 ? '400px' : '200px',
                                    backgroundColor: '#F9F9F9',
                                    borderRadius: '16px',
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        <SearchOutlined sx={{ fontSize: '12px' }} />
                                    </InputAdornment>
                                }}
                                size='small'
                                value={searchText}
                                onChange={(e) => { setSearchText(e.target.value) }} />
                            <Box sx={{ width: '16px' }} />
                            <Typography sx={{ fontWeight: !withPrice ? 'bold' : 'normal', color: withPrice ? 'inherit' : theme.palette.primary.main }}>{t('warehouses.withoutPrices')}</Typography>
                            <Switch checked={withPrice} onChange={(event) => { setWithPrice(event.target.checked) }} inputProps={{ 'aria-label': 'ant design' }} />
                            <Typography sx={{ fontWeight: withPrice ? 'bold' : 'normal', color: !withPrice ? 'inherit' : theme.palette.primary.main }}>{t('warehouses.withPrices')}</Typography>
                        </Stack>
                        <MuiDatagrid
                            apiRef={apiRef}
                            columns={columnsItemsInStock}
                            rows={filteredRows ?? []}
                            editMode="row"
                            checkboxSelection
                            rowSelectionModel={warehouseItemsSelectionModel}
                            onRowSelectionModelChange={(ids) => {
                                if (addItemFromMouseDownRef.current) {
                                    addItemFromMouseDownRef.current = false;
                                    if (ids.length > 0)
                                        setWarehouseItemsSelectionModel(ids);
                                } else {
                                    setWarehouseItemsSelectionModel(ids);
                                }
                            }}
                            sx={{ height: 'calc(100vh - 350px)' }}
                        />
                    </Box>
                </Box>
            </Card>
            <Card sx={{ margin: 3, padding: 3, height: 'calc(100vh - 225px)' }} elevation={3} onDrop={handleDragEnd} onDragOver={(e) => e.preventDefault()}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography sx={{ fontWeight: 'bold', color: 'inherit' }}>{getActionName()}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '300px' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label={t('DateTime')}
                                    inputFormat="DD.MM.YYYY HH:mm"
                                    ampm={false}
                                    value={datetime}
                                    onChange={(newValue) => {
                                        setDatetime(newValue ?? undefined);
                                    }}
                                    renderInput={(params) => <TextField size='small' {...params} style={{ marginTop: (actionType === ActionType.Block || actionType === ActionType.Out) ? '12px' : '-48px', backgroundColor: '#FFFFFF', float: 'left', borderRadius: '20px' }} />}
                                />
                            </LocalizationProvider>
                            {(actionType === ActionType.Block || actionType === ActionType.Out) && <Box sx={{ marginTop: '10px' }}>
                                <BuildingWithWarehouseSelect
                                    label={t('warehouses.projectSelection')}
                                    warehouseId={warehouseId ?? 0}
                                    selectedProjectId={selectedBuildingId}
                                    onChange={(e) => { setSelectedBuildingId(e?.id) }} />
                                <Box sx={{ marginTop: '10px' }}>
                                    <WarehouseByBuildingIdSelect label={t('warehouses.inStock')} warehousesData={warehousesData} selectedWarehouseId={selectedWarehouseId} removeWarehouseId={warehouseId} onChange={(e) => { setSelectedWarehouseId(e?.id) }} />
                                </Box>
                            </Box>}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography sx={{ margin: '0px 8px -10px 8px' }}>{t('warehouses.notes')}</Typography>
                            <TextareaAutosize style={{ width: '100%', minHeight: '110px', maxHeight: '110px', borderRadius: '4px', borderColor: 'lightgray', margin: '8px 0px', resize: 'none' }}
                                minRows={3} maxRows={6}
                                name='notes'
                                value={notes}
                                onChange={(e) => { setNotes(e.target.value) }}
                            />
                        </Box>
                    </Stack>
                    <Box sx={{ marginTop: '8px', overflow: 'auto' }} >
                        {(products ?? []).length !== 0 &&
                            <MuiDatagrid
                                apiRef={gridApiRef}
                                columns={columnsWriteOffProducts}
                                rows={rowsWithFooter ?? []}
                                editMode="row"
                                getRowClassName={(params) => {
                                    const product = params.row as StockItemDTO;
                                    if ((product.totalUnitsCount ?? 0) < (product.unitsCount ?? 0)) {
                                        return 'row-light-red'; // You can set a class name and use the inline style to style the row
                                    }
                                    return '';
                                }}
                                sx={{
                                    '& .row-light-red': {
                                        backgroundColor: '#f8d7da', // Light red color inline
                                    },
                                }}
                            />}
                        {(products ?? []).length === 0 && <NoDataPlaceholder title={t('warehouses.dragSelectedItems')} paragraph={t('warehouses.dragSelectedItemsHere')} />}
                    </Box>
                </Box>
            </Card>
        </CustomSplitter>
        <WarehouseSignatureDialog
            open={openSignature}
            stockInOutListSign={actionType === ActionType.Out && stockInOutListSign}
            onClose={(file, closeTransaction) => {
                setOpenSignature(false);
                if (closeTransaction) {
                    if (actionType !== ActionType.Out || !stockInOutListSign || file)
                        saveListWithItems(true, getActionTypeName(true), file);
                    else
                        setError(t('warehouses.signRequired'));
                }
            }}
        />
        <Snackbar open={(error ?? '') !== ''} autoHideDuration={5000} onClose={() => setError('')}>
            <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
                {error}
            </Alert>
        </Snackbar>
    </Box >
}